/**
 * winbit32Storage — central inventory of every localStorage key the
 * WINBIT32 app owns, with helpers for sweeping them all out.
 *
 * Why this exists
 * ───────────────
 * After the May 2026 privacy audit, sensitive wallet material is
 * held in-memory only. A handful of low-risk preferences and dev
 * flags still live on disk — but the user can hit "Exit" in the
 * Program Manager and expect the browser to be left in a pristine
 * state. To deliver on that promise we need a single place that
 * knows the whole namespace, so a forgotten key never leaks across
 * the Exit boundary.
 *
 * The inventory below is exhaustive: every `localStorage.setItem`
 * call site in the repo writes either a literal listed here OR a
 * prefix listed here. Tests assert that — see
 * `winbit32Storage.test.js`.
 *
 * NOTE: third-party libraries (the Vultisig SDK, wallet-connect
 * adapters, etc.) may write their own keys we don't control. We
 * still wipe them on Exit by scanning EVERY key for a known
 * vendor prefix; the conservative default is to leave anything
 * else alone (an aggressive `localStorage.clear()` would nuke
 * unrelated browser-extension state on the same origin if the
 * extension happened to use `localStorage`).
 */

// Exact-match keys we own.
const EXACT_KEYS = [
	// Imported-token list (cleared on Exit already).
	'winbit32_importedTokens',
	// Cosign relay URL preference.
	'wb32cosign_relay_url',
	// Zcash chain-tip cache used to short-circuit Orchard syncs.
	'wb32:orchard-frost:last-known-tip',
	// Dev flags. Persisted by design (devs toggle them via the
	// browser console) but cleared on explicit Exit so a shared
	// browser doesn't bleed flags across users.
	'wb32:orchard-frost-threshold',
	'wb32:orchard-frost-proper-dkg',
	'wb32:orchard-bake-in',
	// After Dark screensaver choice — only written when the user
	// ticks "Save settings to this computer" in Control Panel → Display.
	'wb32:screensaver-settings',
];

/**
 * Prefix-match keys: anything starting with one of these strings
 * is owned by us. Always paired with a per-key cleanup helper or
 * a guarantee that the per-key payload is in-memory only.
 */
const PREFIX_KEYS = [
	// Window state restoration (`windowState-<name>`).
	'windowState-',
	// Legacy FROST bundle prefixes — sweeps target them
	// individually on boot, but listing them here lets Exit catch
	// any stragglers from older builds.
	'wb32:fromt-frost-bundle:',
	'wb32:fromt-frost-keep-as-is:',
	'wb32:orchard-frost-bundle:',
	'wb32:orchard-frost-keep-choice:',
	'wb32:sapling-frost-bundle:',
	// Legacy Zcash shielded cache prefixes (now in-memory only).
	'wb32:shielded:notes:',
	'wb32:shielded:nullifiers:',
	// Legacy Monero balance/rescan caches (now in-memory only).
	'wb32:monero-balance:',
	'wb32:monero-rescan:',
	// RareSats cache (UI is opt-in via the program; clearing on
	// Exit is consistent with "leave no trace").
	'rareSats_',
	// Messenger sessions — only written when the user explicitly ticks
	// "Remember on this device" (see services/messenger/session.js). The
	// default is file export / in-memory only; this prefix guarantees Exit
	// still sweeps anything a user opted into.
	'wb32:messenger:',
];

/**
 * Return an array of every localStorage key currently allocated
 * by the WINBIT32 namespace. Useful for assertions ("after Exit,
 * this returns []") and for callers that want to act on the list
 * (e.g. show a confirmation dialog).
 */
export function listWinbit32StorageKeys() {
	const out = [];
	if (typeof localStorage === 'undefined') return out;
	for (let i = 0; i < localStorage.length; i += 1) {
		const key = localStorage.key(i);
		if (!key) continue;
		if (EXACT_KEYS.includes(key)) { out.push(key); continue; }
		if (PREFIX_KEYS.some((prefix) => key.startsWith(prefix))) {
			out.push(key);
		}
	}
	return out;
}

/**
 * Remove every WINBIT32-owned key from `localStorage`. Returns
 * the number of keys removed so callers can surface a tidy
 * confirmation message ("Cleared 12 stored items.").
 *
 * Errors on individual `removeItem` calls are swallowed — one
 * locked entry shouldn't block the rest of the wipe. The function
 * is intentionally synchronous so callers can invoke it
 * immediately before a `window.location.reload()` without racing
 * the navigation.
 */
export function clearAllWinbit32Storage() {
	if (typeof localStorage === 'undefined') return 0;
	const doomed = listWinbit32StorageKeys();
	for (const key of doomed) {
		try { localStorage.removeItem(key); } catch { /* per-key noop */ }
	}
	return doomed.length;
}

// Exposed for tests.
export const __test_internals = {
	EXACT_KEYS,
	PREFIX_KEYS,
};
